<script setup lang="ts">
// The whole suite-Lumi mount in one place (ADR 0027): wraps the headless
// @lumi/ui LumiAssistantPanel and builds its transport + persistence keys from a
// handful of app inputs, so each UI's AppChrome contributes a single <LumiDock/>
// in its AppShell #dock slot instead of copy-pasting the transport wiring. Lives
// in app-kit because it needs vue-router + axios (the wider peer surface @lumi/ui
// is not allowed).
import { computed, nextTick, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { LumiAssistantPanel, openLumiDock } from '@lumi/ui';
import type { LumiModuleKey, ModuleSwitchTarget, AssistantLabels } from '@lumi/ui';
import type { AxiosInstance } from 'axios';
import { createAssistantTransport, assistantStorageKeys, migrateLegacyAssistantKeys } from '../utils/assistantTransport';
import { lumiAvailabilityVersion } from '../utils/lumiAvailability';

const props = defineProps<{
  /** The app's tenant-scoped axios instance for `/api/assistant`. */
  api: AxiosInstance;
  /** This app's module key (accent + cross-module navigation origin). */
  module: LumiModuleKey;
  /** The module-switcher targets (for cross-module navigation). */
  moduleTargets: ModuleSwitchTarget[];
  /** Current identity: tenant and user scope persistence; tenant, email and
   *  locale inform page context. Fields accept null (store getters are commonly
   *  `string | null`). `userId` is the signed-in user's id — or, while a demo
   *  persona is active, the host's stable persona key — and it is what gates the
   *  dock and keys its storage (issue 698): a phone-only account has no `email`. */
  identity: { tenantSlug?: string | null; userId?: string | null; email?: string | null; locale?: string | null };
  /**
   * Accepted for compatibility, and inert in a converted app: since issue 195 (A10)
   * no app in the suite has an error-surfacing axios interceptor left to opt out of.
   * A write chooses where its failure is reported at its own call site, through
   * `useApiMutation`'s `errorChannel`.
   */
  suppressGlobalError?: boolean;
  /** Optional full i18n override for the chrome labels; merged over the locale default. */
  labels?: Partial<AssistantLabels>;
  /**
   * Leave a first-time visitor UNGREETED (#419).
   *
   * The one caller is a workspace in suite demo mode: the people signing into a
   * demo are meeting a fictional company, not starting with the product, and a
   * welcome addressed to them is addressed to the wrong occasion. They pay for it
   * by never being greeted — the "first time" is spent whether or not anybody
   * said anything, because it is the conversation row and not a browser flag.
   * That is the trade the issue asks for.
   */
  suppressGreeting?: boolean;
  /**
   * Open Lumi in the dock when a browser session starts (a workspace in suite
   * demo mode, the same caller as `suppressGreeting`): a demo visitor should
   * see Lumi without hunting for it. Minimizing it is remembered for the session.
   */
  openOnArrival?: boolean;
}>();

// The panel's built-in defaults are German (product default, `DEFAULT_LABELS`
// in LumiAssistantPanel.vue) — that stays the component-lib default so it
// keeps working standalone. This is the 9-locale bundle (#275, D3 + drift
// adaptation A12): every host already mounts LumiDock with `identity`, so the
// locale resolution lives here rather than as a `labels` prop every one of
// the eight AppChrome hosts would have to start passing. `title`/`ai`/
// `launcherLabel` stay the brand name "Lumi" everywhere and are omitted.
// Registers per conventions.md: de=Du (the component-lib default), fr=vous,
// pl=Pan/Pani/Państwo, pt=neutral (labels own bubble as "Eu"/"Me" rather than
// committing to tu/você), es/it/nl/sv informal. apprentice-ui needs no
// carve-out: it stays de-only by virtue of its users' locale, not a branch
// here.
//
// `greeting` (#419) is the one label a host may deliberately BLANK: an empty
// string means no opening line and no self-opening panel, which is how a
// workspace in demo mode is left alone (see `suppressGreeting`).
const LUMI_LABELS: Record<string, Partial<AssistantLabels>> = {
  en: {
    you: 'You',
    actions: 'Actions',
    needsApproval: 'Confirmation needed',
    approve: 'Approve',
    cancel: 'Cancel',
    runStatusSucceeded: 'Done',
    runStatusFailed: 'Not completed',
    runStatusCancelled: 'Discarded',
    runStatusRunning: 'Running …',
    runStatusRefused: 'Declined',
    runStatusExpired: 'Expired',
    runDetailsShow: 'Show details',
    runDetailsHide: 'Hide details',
    runOpen: 'Open',
    runApproveAll: 'Approve all',
    newMessage: 'New message',
    inputPlaceholder: 'Ask lumi …',
    send: 'Send',
    sending: 'Sending …',
    loading: 'Loading …',
    newSessionLabel: 'New conversation',
    newSessionTitle: 'Start a new conversation?',
    newSessionBody: 'The current conversation with lumi will be permanently deleted.',
    minimizeLabel: 'Minimize',
    dockLabel: 'Dock',
    floatLabel: 'Float',
    resizeLabel: 'Resize width',
    sendError: 'Failed to send.',
    actionError: 'That action could not be completed.',
    draftLoading: 'Loading details …',
    draftCandidate: 'Candidate',
    draftApplication: 'Application number',
    draftJob: 'Job',
    draftSummary: 'Summary',
    draftSubject: 'Subject',
    draftBody: 'Body',
    draftEdited: 'Edited',
    draftErrorNotAvailable: 'There is no draft to review for this action.',
    draftErrorNotFound: 'The draft could not be found.',
    draftErrorNoMessage: 'This draft no longer contains a message you can approve.',
    draftErrorPermission: 'You do not have permission to view this draft.',
    draftErrorGeneric: 'The draft could not be loaded.',
    approveErrorPermission: 'You do not have permission to approve this.',
    approveErrorGeneric: 'This could not be approved; nothing was sent.',
    feedbackUp: 'Helpful',
    feedbackDown: 'Not helpful',
    feedbackThanks: 'Thanks for the feedback.',
    feedbackFailed: 'Your feedback could not be saved.',
    feedbackReasonTitle: 'What was wrong?',
    feedbackReasonWrongResult: 'Wrong result',
    feedbackReasonDidNothing: 'Nothing happened',
    feedbackReasonMisunderstood: 'Misunderstood me',
    feedbackReasonTooSlow: 'Too slow',
    feedbackReasonOther: 'Something else',
    feedbackTextPlaceholder: 'Optional: what should have happened?',
    feedbackSubmit: 'Submit',
    feedbackDismiss: 'Discard',
    greeting: 'Welcome! I\'m lumi. I\'m here to help and to answer any questions you have while you find your way around — and to support you with your day-to-day work.',
    emptyThread: 'How can I help you?',
    attach: 'Attach file',
    takePhoto: 'Take photo',
    dropHere: 'Drop files here',
    attachRemove: 'Remove',
    attachUploading: 'Uploading …',
    attachErrorType: 'Only PDF, DOCX, PNG and JPEG',
    attachErrorSize: 'Larger than 10 MB',
    attachErrorTooMany: 'At most 5 files per message',
    attachErrorScan: 'Refused by the virus scan',
    attachErrorGeneric: 'Could not be uploaded',
  },
  fr: {
    you: 'Vous',
    actions: 'Actions',
    needsApproval: 'Confirmation requise',
    approve: 'Valider',
    cancel: 'Annuler',
    runStatusSucceeded: 'Effectué',
    runStatusFailed: 'Non effectué',
    runStatusCancelled: 'Abandonné',
    runStatusRunning: 'En cours …',
    runStatusRefused: 'Refusé',
    runStatusExpired: 'Expiré',
    runDetailsShow: 'Afficher les détails',
    runDetailsHide: 'Masquer les détails',
    runOpen: 'Ouvrir',
    runApproveAll: 'Tout valider',
    newMessage: 'Nouveau message',
    inputPlaceholder: 'Demandez à lumi …',
    send: 'Envoyer',
    sending: 'Envoi …',
    loading: 'Chargement …',
    newSessionLabel: 'Nouvelle conversation',
    newSessionTitle: 'Démarrer une nouvelle conversation ?',
    newSessionBody: 'La conversation actuelle avec lumi sera définitivement supprimée.',
    minimizeLabel: 'Réduire',
    dockLabel: 'Ancrer',
    floatLabel: 'Détacher',
    resizeLabel: 'Ajuster la largeur',
    sendError: "Échec de l'envoi.",
    actionError: "L'action n'a pas pu être effectuée.",
    draftLoading: 'Chargement des détails …',
    draftCandidate: 'Candidat(e)',
    draftApplication: 'Numéro de candidature',
    draftJob: 'Poste',
    draftSummary: 'Résumé',
    draftSubject: 'Objet',
    draftBody: 'Corps du message',
    draftEdited: 'Modifié',
    draftErrorNotAvailable: 'Aucun brouillon à consulter pour cette action.',
    draftErrorNotFound: 'Le brouillon est introuvable.',
    draftErrorNoMessage: 'Ce brouillon ne contient plus de message que vous pouvez valider.',
    draftErrorPermission: "Vous n'êtes pas autorisé(e) à consulter ce brouillon.",
    draftErrorGeneric: "Le brouillon n'a pas pu être chargé.",
    approveErrorPermission: "Vous n'êtes pas autorisé(e) à valider cette action.",
    approveErrorGeneric: "La validation a échoué ; rien n'a été envoyé.",
    feedbackUp: 'Utile',
    feedbackDown: 'Pas utile',
    feedbackThanks: 'Merci pour votre retour.',
    feedbackFailed: 'Votre retour n\'a pas pu être enregistré.',
    feedbackReasonTitle: 'Qu\'est-ce qui n\'allait pas ?',
    feedbackReasonWrongResult: 'Résultat incorrect',
    feedbackReasonDidNothing: 'Il ne s\'est rien passé',
    feedbackReasonMisunderstood: 'Mal compris',
    feedbackReasonTooSlow: 'Trop lent',
    feedbackReasonOther: 'Autre chose',
    feedbackTextPlaceholder: 'Facultatif : que devait-il se passer ?',
    feedbackSubmit: 'Envoyer',
    feedbackDismiss: 'Abandonner',
    greeting: 'Bienvenue ! Je suis lumi. Je suis là pour vous aider et répondre à vos questions pendant que vous prenez vos marques — et pour vous accompagner dans votre travail quotidien.',
    emptyThread: 'Comment puis-je vous aider ?',
    attach: 'Joindre un fichier',
    takePhoto: 'Prendre une photo',
    dropHere: 'Déposez les fichiers ici',
    attachRemove: 'Retirer',
    attachUploading: 'Envoi en cours …',
    attachErrorType: 'Uniquement PDF, DOCX, PNG et JPEG',
    attachErrorSize: 'Plus de 10 Mo',
    attachErrorTooMany: '5 fichiers au maximum par message',
    attachErrorScan: 'Refusé par l’antivirus',
    attachErrorGeneric: 'Envoi impossible',
  },
  es: {
    you: 'Tú',
    actions: 'Acciones',
    needsApproval: 'Confirmación necesaria',
    approve: 'Aprobar',
    cancel: 'Cancelar',
    runStatusSucceeded: 'Realizado',
    runStatusFailed: 'No realizado',
    runStatusCancelled: 'Descartado',
    runStatusRunning: 'En curso …',
    runStatusRefused: 'Rechazado',
    runStatusExpired: 'Caducado',
    runDetailsShow: 'Mostrar detalles',
    runDetailsHide: 'Ocultar detalles',
    runOpen: 'Abrir',
    runApproveAll: 'Aprobar todo',
    newMessage: 'Mensaje nuevo',
    inputPlaceholder: 'Pregunta a lumi …',
    send: 'Enviar',
    sending: 'Enviando …',
    loading: 'Cargando …',
    newSessionLabel: 'Nueva conversación',
    newSessionTitle: '¿Iniciar una nueva conversación?',
    newSessionBody: 'La conversación actual con lumi se eliminará definitivamente.',
    minimizeLabel: 'Minimizar',
    dockLabel: 'Anclar',
    floatLabel: 'Flotar',
    resizeLabel: 'Ajustar el ancho',
    sendError: 'Error al enviar.',
    actionError: 'No se pudo ejecutar la acción.',
    draftLoading: 'Cargando detalles …',
    draftCandidate: 'Candidato/a',
    draftApplication: 'Número de solicitud',
    draftJob: 'Puesto',
    draftSummary: 'Resumen',
    draftSubject: 'Asunto',
    draftBody: 'Cuerpo',
    draftEdited: 'Editado',
    draftErrorNotAvailable: 'No hay ningún borrador para revisar en esta acción.',
    draftErrorNotFound: 'No se encontró el borrador.',
    draftErrorNoMessage: 'Este borrador ya no contiene un mensaje que puedas aprobar.',
    draftErrorPermission: 'No tienes permiso para ver este borrador.',
    draftErrorGeneric: 'No se pudo cargar el borrador.',
    approveErrorPermission: 'No tienes permiso para aprobar esto.',
    approveErrorGeneric: 'No se pudo aprobar; no se envió nada.',
    feedbackUp: 'Útil',
    feedbackDown: 'No útil',
    feedbackThanks: 'Gracias por tu comentario.',
    feedbackFailed: 'No se pudo guardar tu comentario.',
    feedbackReasonTitle: '¿Qué falló?',
    feedbackReasonWrongResult: 'Resultado incorrecto',
    feedbackReasonDidNothing: 'No pasó nada',
    feedbackReasonMisunderstood: 'Me entendió mal',
    feedbackReasonTooSlow: 'Demasiado lento',
    feedbackReasonOther: 'Otra cosa',
    feedbackTextPlaceholder: 'Opcional: ¿qué debería haber pasado?',
    feedbackSubmit: 'Enviar',
    feedbackDismiss: 'Descartar',
    greeting: '¡Bienvenido/a! Soy lumi. Estoy aquí para ayudarte y responder a tus preguntas mientras te familiarizas con el sistema, y también para apoyarte en tu trabajo diario.',
    emptyThread: '¿En qué puedo ayudarte?',
    attach: 'Adjuntar archivo',
    takePhoto: 'Hacer una foto',
    dropHere: 'Suelta los archivos aquí',
    attachRemove: 'Quitar',
    attachUploading: 'Subiendo …',
    attachErrorType: 'Solo PDF, DOCX, PNG y JPEG',
    attachErrorSize: 'Más de 10 MB',
    attachErrorTooMany: 'Máximo 5 archivos por mensaje',
    attachErrorScan: 'Rechazado por el antivirus',
    attachErrorGeneric: 'No se pudo subir',
  },
  nl: {
    you: 'Jij',
    actions: 'Acties',
    needsApproval: 'Bevestiging nodig',
    approve: 'Goedkeuren',
    cancel: 'Annuleren',
    runStatusSucceeded: 'Uitgevoerd',
    runStatusFailed: 'Niet uitgevoerd',
    runStatusCancelled: 'Verworpen',
    runStatusRunning: 'Bezig …',
    runStatusRefused: 'Geweigerd',
    runStatusExpired: 'Verlopen',
    runDetailsShow: 'Details tonen',
    runDetailsHide: 'Details verbergen',
    runOpen: 'Openen',
    runApproveAll: 'Alles goedkeuren',
    newMessage: 'Nieuw bericht',
    inputPlaceholder: 'Vraag het lumi …',
    send: 'Versturen',
    sending: 'Versturen …',
    loading: 'Laden …',
    newSessionLabel: 'Nieuw gesprek',
    newSessionTitle: 'Nieuw gesprek starten?',
    newSessionBody: 'Het huidige gesprek met lumi wordt definitief verwijderd.',
    minimizeLabel: 'Minimaliseren',
    dockLabel: 'Vastzetten',
    floatLabel: 'Losmaken',
    resizeLabel: 'Breedte aanpassen',
    sendError: 'Versturen mislukt.',
    actionError: 'De actie kon niet worden uitgevoerd.',
    draftLoading: 'Details laden …',
    draftCandidate: 'Kandidaat',
    draftApplication: 'Sollicitatienummer',
    draftJob: 'Vacature',
    draftSummary: 'Samenvatting',
    draftSubject: 'Onderwerp',
    draftBody: 'Tekst',
    draftEdited: 'Bewerkt',
    draftErrorNotAvailable: 'Er is geen concept om te bekijken voor deze actie.',
    draftErrorNotFound: 'Het concept is niet gevonden.',
    draftErrorNoMessage: 'Dit concept bevat geen bericht meer dat je kunt goedkeuren.',
    draftErrorPermission: 'Je mag dit concept niet bekijken.',
    draftErrorGeneric: 'Het concept kon niet worden geladen.',
    approveErrorPermission: 'Je mag dit niet goedkeuren.',
    approveErrorGeneric: 'Goedkeuren is mislukt; er is niets verzonden.',
    feedbackUp: 'Nuttig',
    feedbackDown: 'Niet nuttig',
    feedbackThanks: 'Bedankt voor je feedback.',
    feedbackFailed: 'Je feedback kon niet worden opgeslagen.',
    feedbackReasonTitle: 'Wat klopte er niet?',
    feedbackReasonWrongResult: 'Verkeerd resultaat',
    feedbackReasonDidNothing: 'Er gebeurde niets',
    feedbackReasonMisunderstood: 'Verkeerd begrepen',
    feedbackReasonTooSlow: 'Te traag',
    feedbackReasonOther: 'Iets anders',
    feedbackTextPlaceholder: 'Optioneel: wat had er moeten gebeuren?',
    feedbackSubmit: 'Versturen',
    feedbackDismiss: 'Verwerpen',
    greeting: 'Welkom! Ik ben lumi. Ik help je graag en beantwoord je vragen terwijl je wegwijs wordt — en ik ondersteun je ook bij je dagelijkse werk.',
    emptyThread: 'Waarmee kan ik je helpen?',
    attach: 'Bestand bijvoegen',
    takePhoto: 'Foto maken',
    dropHere: 'Zet bestanden hier neer',
    attachRemove: 'Verwijderen',
    attachUploading: 'Wordt geüpload …',
    attachErrorType: 'Alleen PDF, DOCX, PNG en JPEG',
    attachErrorSize: 'Groter dan 10 MB',
    attachErrorTooMany: 'Maximaal 5 bestanden per bericht',
    attachErrorScan: 'Geweigerd door de virusscan',
    attachErrorGeneric: 'Kon niet worden geüpload',
  },
  pl: {
    you: 'Ja',
    actions: 'Działania',
    needsApproval: 'Wymagane potwierdzenie',
    approve: 'Zatwierdź',
    cancel: 'Anuluj',
    runStatusSucceeded: 'Wykonano',
    runStatusFailed: 'Nie wykonano',
    runStatusCancelled: 'Odrzucono',
    runStatusRunning: 'W toku …',
    runStatusRefused: 'Odmówiono',
    runStatusExpired: 'Wygasło',
    runDetailsShow: 'Pokaż szczegóły',
    runDetailsHide: 'Ukryj szczegóły',
    runOpen: 'Otwórz',
    runApproveAll: 'Zatwierdź wszystko',
    newMessage: 'Nowa wiadomość',
    inputPlaceholder: 'Zapytaj lumi …',
    send: 'Wyślij',
    sending: 'Wysyłanie …',
    loading: 'Wczytywanie …',
    newSessionLabel: 'Nowa rozmowa',
    newSessionTitle: 'Rozpocząć nową rozmowę?',
    newSessionBody: 'Bieżąca rozmowa z lumi zostanie trwale usunięta.',
    minimizeLabel: 'Minimalizuj',
    dockLabel: 'Zadokuj',
    floatLabel: 'Odczep',
    resizeLabel: 'Dostosowanie szerokości',
    sendError: 'Nie udało się wysłać.',
    actionError: 'Nie udało się wykonać tej czynności.',
    draftLoading: 'Wczytywanie szczegółów …',
    draftCandidate: 'Kandydat(ka)',
    draftApplication: 'Numer aplikacji',
    draftJob: 'Stanowisko',
    draftSummary: 'Podsumowanie',
    draftSubject: 'Temat',
    draftBody: 'Treść',
    draftEdited: 'Edytowano',
    draftErrorNotAvailable: 'Dla tej czynności nie ma wersji roboczej do przejrzenia.',
    draftErrorNotFound: 'Nie znaleziono wersji roboczej.',
    draftErrorNoMessage: 'Ta wersja robocza nie zawiera już treści, którą można zatwierdzić.',
    draftErrorPermission: 'Nie mają Państwo uprawnień do wyświetlenia tej wersji roboczej.',
    draftErrorGeneric: 'Nie udało się wczytać wersji roboczej.',
    approveErrorPermission: 'Nie mają Państwo uprawnień do zatwierdzenia tego działania.',
    approveErrorGeneric: 'Nie udało się zatwierdzić; nic nie zostało wysłane.',
    feedbackUp: 'Pomocne',
    feedbackDown: 'Niepomocne',
    feedbackThanks: 'Dziękujemy za opinię.',
    feedbackFailed: 'Nie udało się zapisać opinii.',
    feedbackReasonTitle: 'Co było nie tak?',
    feedbackReasonWrongResult: 'Błędny wynik',
    feedbackReasonDidNothing: 'Nic się nie stało',
    feedbackReasonMisunderstood: 'Źle zrozumiane',
    feedbackReasonTooSlow: 'Za wolno',
    feedbackReasonOther: 'Coś innego',
    feedbackTextPlaceholder: 'Opcjonalnie: co powinno się wydarzyć?',
    feedbackSubmit: 'Wyślij',
    feedbackDismiss: 'Odrzuć',
    greeting: 'Witamy! Nazywam się lumi. Chętnie pomogę i odpowiem na pytania, gdy będą Państwo poznawać system — a także wesprę Państwa w codziennej pracy.',
    emptyThread: 'W czym mogę Państwu pomóc?',
    attach: 'Dołącz plik',
    takePhoto: 'Zrób zdjęcie',
    dropHere: 'Upuść pliki tutaj',
    attachRemove: 'Usuń',
    attachUploading: 'Przesyłanie …',
    attachErrorType: 'Tylko PDF, DOCX, PNG i JPEG',
    attachErrorSize: 'Większy niż 10 MB',
    attachErrorTooMany: 'Maksymalnie 5 plików na wiadomość',
    attachErrorScan: 'Odrzucony przez skaner antywirusowy',
    attachErrorGeneric: 'Nie udało się przesłać',
  },
  sv: {
    you: 'Du',
    actions: 'Åtgärder',
    needsApproval: 'Bekräftelse krävs',
    approve: 'Godkänn',
    cancel: 'Avbryt',
    runStatusSucceeded: 'Utfört',
    runStatusFailed: 'Ej utfört',
    runStatusCancelled: 'Avvisat',
    runStatusRunning: 'Pågår …',
    runStatusRefused: 'Nekat',
    runStatusExpired: 'Har gått ut',
    runDetailsShow: 'Visa detaljer',
    runDetailsHide: 'Dölj detaljer',
    runOpen: 'Öppna',
    runApproveAll: 'Godkänn allt',
    newMessage: 'Nytt meddelande',
    inputPlaceholder: 'Fråga lumi …',
    send: 'Skicka',
    sending: 'Skickar …',
    loading: 'Läser in …',
    newSessionLabel: 'Ny konversation',
    newSessionTitle: 'Starta en ny konversation?',
    newSessionBody: 'Den aktuella konversationen med lumi kommer att raderas permanent.',
    minimizeLabel: 'Minimera',
    dockLabel: 'Docka',
    floatLabel: 'Frikoppla',
    resizeLabel: 'Justera bredden',
    sendError: 'Det gick inte att skicka.',
    actionError: 'Åtgärden kunde inte utföras.',
    draftLoading: 'Läser in detaljer …',
    draftCandidate: 'Kandidat',
    draftApplication: 'Ansökningsnummer',
    draftJob: 'Tjänst',
    draftSummary: 'Sammanfattning',
    draftSubject: 'Ämne',
    draftBody: 'Text',
    draftEdited: 'Redigerad',
    draftErrorNotAvailable: 'Det finns inget utkast att granska för den här åtgärden.',
    draftErrorNotFound: 'Utkastet kunde inte hittas.',
    draftErrorNoMessage: 'Det här utkastet innehåller inte längre något meddelande som du kan godkänna.',
    draftErrorPermission: 'Du har inte behörighet att visa det här utkastet.',
    draftErrorGeneric: 'Utkastet kunde inte läsas in.',
    approveErrorPermission: 'Du har inte behörighet att godkänna det här.',
    approveErrorGeneric: 'Godkännandet misslyckades; ingenting skickades.',
    feedbackUp: 'Hjälpsamt',
    feedbackDown: 'Inte hjälpsamt',
    feedbackThanks: 'Tack för din återkoppling.',
    feedbackFailed: 'Din återkoppling kunde inte sparas.',
    feedbackReasonTitle: 'Vad blev fel?',
    feedbackReasonWrongResult: 'Fel resultat',
    feedbackReasonDidNothing: 'Ingenting hände',
    feedbackReasonMisunderstood: 'Missförstod mig',
    feedbackReasonTooSlow: 'För långsamt',
    feedbackReasonOther: 'Något annat',
    feedbackTextPlaceholder: 'Valfritt: vad borde ha hänt?',
    feedbackSubmit: 'Skicka',
    feedbackDismiss: 'Kasta',
    greeting: 'Välkommen! Jag heter lumi. Jag hjälper dig gärna och svarar på dina frågor medan du hittar rätt — och stöttar dig i det dagliga arbetet.',
    emptyThread: 'Hur kan jag hjälpa dig?',
    attach: 'Bifoga fil',
    takePhoto: 'Ta foto',
    dropHere: 'Släpp filer här',
    attachRemove: 'Ta bort',
    attachUploading: 'Laddas upp …',
    attachErrorType: 'Endast PDF, DOCX, PNG och JPEG',
    attachErrorSize: 'Större än 10 MB',
    attachErrorTooMany: 'Högst 5 filer per meddelande',
    attachErrorScan: 'Stoppad av virusskanningen',
    attachErrorGeneric: 'Kunde inte laddas upp',
  },
  it: {
    you: 'Tu',
    actions: 'Azioni',
    needsApproval: 'Conferma necessaria',
    approve: 'Approva',
    cancel: 'Annulla',
    runStatusSucceeded: 'Eseguito',
    runStatusFailed: 'Non eseguito',
    runStatusCancelled: 'Scartato',
    runStatusRunning: 'In corso …',
    runStatusRefused: 'Rifiutato',
    runStatusExpired: 'Scaduto',
    runDetailsShow: 'Mostra dettagli',
    runDetailsHide: 'Nascondi dettagli',
    runOpen: 'Apri',
    runApproveAll: 'Approva tutto',
    newMessage: 'Nuovo messaggio',
    inputPlaceholder: 'Chiedi a lumi …',
    send: 'Invia',
    sending: 'Invio …',
    loading: 'Caricamento …',
    newSessionLabel: 'Nuova conversazione',
    newSessionTitle: 'Avviare una nuova conversazione?',
    newSessionBody: 'La conversazione attuale con lumi verrà eliminata definitivamente.',
    minimizeLabel: 'Riduci a icona',
    dockLabel: 'Aggancia',
    floatLabel: 'Sgancia',
    resizeLabel: 'Regola la larghezza',
    sendError: 'Invio non riuscito.',
    actionError: "Impossibile eseguire l'azione.",
    draftLoading: 'Caricamento dei dettagli …',
    draftCandidate: 'Candidato/a',
    draftApplication: 'Numero candidatura',
    draftJob: 'Posizione',
    draftSummary: 'Riepilogo',
    draftSubject: 'Oggetto',
    draftBody: 'Testo',
    draftEdited: 'Modificato',
    draftErrorNotAvailable: "Non c'è nessuna bozza da rivedere per questa azione.",
    draftErrorNotFound: 'Bozza non trovata.',
    draftErrorNoMessage: 'Questa bozza non contiene più un messaggio che puoi approvare.',
    draftErrorPermission: 'Non hai il permesso di visualizzare questa bozza.',
    draftErrorGeneric: 'Impossibile caricare la bozza.',
    approveErrorPermission: 'Non hai il permesso di approvare questa azione.',
    approveErrorGeneric: 'Impossibile approvare: non è stato inviato nulla.',
    feedbackUp: 'Utile',
    feedbackDown: 'Non utile',
    feedbackThanks: 'Grazie per il riscontro.',
    feedbackFailed: 'Non è stato possibile salvare il tuo riscontro.',
    feedbackReasonTitle: 'Che cosa non andava?',
    feedbackReasonWrongResult: 'Risultato sbagliato',
    feedbackReasonDidNothing: 'Non è successo nulla',
    feedbackReasonMisunderstood: 'Mi ha frainteso',
    feedbackReasonTooSlow: 'Troppo lento',
    feedbackReasonOther: 'Altro',
    feedbackTextPlaceholder: 'Facoltativo: che cosa sarebbe dovuto succedere?',
    feedbackSubmit: 'Invia',
    feedbackDismiss: 'Scarta',
    greeting: 'Benvenuto/a! Sono lumi. Sono qui per aiutarti e rispondere alle tue domande mentre prendi confidenza con il sistema, e per supportarti nel lavoro di ogni giorno.',
    emptyThread: 'Come posso aiutarti?',
    attach: 'Allega file',
    takePhoto: 'Scatta foto',
    dropHere: 'Trascina qui i file',
    attachRemove: 'Rimuovi',
    attachUploading: 'Caricamento …',
    attachErrorType: 'Solo PDF, DOCX, PNG e JPEG',
    attachErrorSize: 'Più di 10 MB',
    attachErrorTooMany: 'Al massimo 5 file per messaggio',
    attachErrorScan: 'Rifiutato dall’antivirus',
    attachErrorGeneric: 'Impossibile caricare',
  },
  pt: {
    you: 'Eu',
    actions: 'Ações',
    needsApproval: 'Confirmação necessária',
    approve: 'Aprovar',
    cancel: 'Cancelar',
    runStatusSucceeded: 'Concluído',
    runStatusFailed: 'Não concluído',
    runStatusCancelled: 'Descartado',
    runStatusRunning: 'Em curso …',
    runStatusRefused: 'Recusado',
    runStatusExpired: 'Expirado',
    runDetailsShow: 'Mostrar detalhes',
    runDetailsHide: 'Ocultar detalhes',
    runOpen: 'Abrir',
    runApproveAll: 'Aprovar tudo',
    newMessage: 'Nova mensagem',
    inputPlaceholder: 'Perguntar à lumi …',
    send: 'Enviar',
    sending: 'A enviar …',
    loading: 'A carregar …',
    newSessionLabel: 'Nova conversa',
    newSessionTitle: 'Iniciar uma nova conversa?',
    newSessionBody: 'A conversa atual com a lumi será eliminada definitivamente.',
    minimizeLabel: 'Minimizar',
    dockLabel: 'Fixar',
    floatLabel: 'Destacar',
    resizeLabel: 'Ajustar a largura',
    sendError: 'Falha ao enviar.',
    actionError: 'Não foi possível executar a ação.',
    draftLoading: 'A carregar os detalhes …',
    draftCandidate: 'Candidato/a',
    draftApplication: 'Número da candidatura',
    draftJob: 'Vaga',
    draftSummary: 'Resumo',
    draftSubject: 'Assunto',
    draftBody: 'Texto',
    draftEdited: 'Editado',
    draftErrorNotAvailable: 'Não há rascunho para revisão nesta ação.',
    draftErrorNotFound: 'Rascunho não encontrado.',
    draftErrorNoMessage: 'Este rascunho já não contém uma mensagem que possa ser aprovada.',
    draftErrorPermission: 'Não há permissão para ver este rascunho.',
    draftErrorGeneric: 'Não foi possível carregar o rascunho.',
    approveErrorPermission: 'Não há permissão para aprovar esta ação.',
    approveErrorGeneric: 'Não foi possível aprovar; nada foi enviado.',
    feedbackUp: 'Útil',
    feedbackDown: 'Não útil',
    feedbackThanks: 'Obrigado pelo comentário.',
    feedbackFailed: 'Não foi possível guardar o comentário.',
    feedbackReasonTitle: 'O que correu mal?',
    feedbackReasonWrongResult: 'Resultado errado',
    feedbackReasonDidNothing: 'Não aconteceu nada',
    feedbackReasonMisunderstood: 'Percebeu mal',
    feedbackReasonTooSlow: 'Demasiado lento',
    feedbackReasonOther: 'Outra coisa',
    feedbackTextPlaceholder: 'Opcional: o que deveria ter acontecido?',
    feedbackSubmit: 'Enviar',
    feedbackDismiss: 'Descartar',
    greeting: 'Bem-vindo/a! Sou o lumi. Estou aqui para ajudar e responder às suas questões enquanto se familiariza com o sistema — e para o apoiar no trabalho do dia a dia.',
    emptyThread: 'Como posso ajudar?',
    attach: 'Anexar ficheiro',
    takePhoto: 'Tirar foto',
    dropHere: 'Largue os ficheiros aqui',
    attachRemove: 'Remover',
    attachUploading: 'A carregar …',
    attachErrorType: 'Apenas PDF, DOCX, PNG e JPEG',
    attachErrorSize: 'Maior que 10 MB',
    attachErrorTooMany: 'No máximo 5 ficheiros por mensagem',
    attachErrorScan: 'Recusado pelo antivírus',
    attachErrorGeneric: 'Não foi possível carregar',
  },
};
// The verb on the "new conversation" confirm button: the transport's confirm()
// carries only a title and a body, and the panel's own title/body are shared
// with component-lib, so the destructive verb lives here, per locale (German is
// the default, as for the labels above).
const NEW_SESSION_CONFIRM: Record<string, string> = {
  de: 'Löschen und neu starten',
  en: 'Delete and start new',
  fr: 'Supprimer et recommencer',
  es: 'Eliminar y empezar de nuevo',
  nl: 'Verwijderen en opnieuw beginnen',
  pl: 'Usuń i zacznij od nowa',
  sv: 'Radera och börja om',
  it: 'Elimina e ricomincia',
  pt: 'Eliminar e recomeçar',
};
const newSessionConfirmLabel = () => NEW_SESSION_CONFIRM[(props.identity.locale || '').slice(0, 2).toLowerCase()] ?? NEW_SESSION_CONFIRM.de;
const labels = computed<Partial<AssistantLabels> | undefined>(() => {
  const loc = (props.identity.locale || '').slice(0, 2).toLowerCase();
  // German (`de`) has no entry: the panel's own DEFAULT_LABELS already is
  // German, so the localeDefault stays undefined and DEFAULT_LABELS alone
  // applies — same behavior as before this bundle existed.
  const localeDefault = LUMI_LABELS[loc];
  const merged = props.labels ? { ...(localeDefault || {}), ...props.labels } : localeDefault;
  // An empty greeting is how the panel is told not to greet at all (#419) — the
  // suppression is one value, not a second prop threaded down to it.
  if (props.suppressGreeting) return { ...(merged || {}), greeting: '' };
  return merged;
});

const router = useRouter();
// Normalise null -> undefined at the boundary so the downstream helpers see the
// `string | undefined` they expect.
const ident = () => ({
  tenantSlug: props.identity.tenantSlug ?? undefined,
  userId: props.identity.userId ?? undefined,
  email: props.identity.email ?? undefined,
  locale: props.identity.locale ?? undefined,
});
// Only mount (and therefore fetch a conversation) once there is an authenticated
// user AND a resolved tenant. AppChrome renders on unauthenticated routes too
// (e.g. /login), and Lumi is a per-user assistant — without the user check it
// would call /api/assistant on the login page, whose 401 trips the app's 401
// auth-refresh ladder and, across every page load, user-api's rate limiter (a
// cascade that broke login in e2e). `userId` is only populated once the auth
// store has a real session. It used to be `email`, which a phone-only account
// does not have (issue 698): the dock never mounted for one.
// The tenantSlug half matters for multi-tenant hosts (workspace-ui): there,
// `identity.tenantSlug` resolves asynchronously from a tenant list fetch, a
// beat after the user — the api instance's request interceptor reads the slug
// synchronously off that same not-yet-settled state, so a request fired in
// that gap goes out with no `X-Tenant-Slug` and TenantMembershipGuard 400s it.
// Every single-tenant host already has its slug at mount (from the URL/local
// tenant store), so this costs them nothing.
const isAuthenticated = computed(() => !!ident().userId && !!ident().tenantSlug);
const keys = computed(() => assistantStorageKeys(ident().tenantSlug, ident().userId));
// Values an earlier build stored under the address-keyed names move over once,
// before the panel reads its keys (it mounts only after this ran for the same
// identity, since both hang off the same reactive inputs).
watch(
  () => [ident().tenantSlug, ident().userId, ident().email] as const,
  ([tenantSlug, userId, email]) => migrateLegacyAssistantKeys(tenantSlug, userId, email),
  { immediate: true },
);
const transport = createAssistantTransport({
  api: props.api,
  router,
  module: props.module,
  route: () => router.currentRoute.value.fullPath,
  moduleTargets: () => props.moduleTargets,
  identity: ident,
  suppressGlobalError: props.suppressGlobalError,
  confirmLabel: newSessionConfirmLabel,
});

// Whether Lumi may render at all (make brief ai-switch-notify-launcher,
// outcome invariant 3): tenant preference AND sysadmin floor, resolved from
// assistant-api. `null` is the not-yet-known state — the panel stays unmounted
// through it, so an unavailable tenant never gets a flash of the launcher
// before it disappears, and a check that fails (network, 401 mid-refresh, …)
// fails CLOSED rather than leaving a dead-end icon on screen.
//
// It used to be resolved ONCE per session, on the claim that "this flag does
// not flip while a tenant is on the page". It does (issue 364 F29):
//   · an operator or a billing manager switching AI on or off flips it, and
//     the open page never noticed — only a reload did;
//   · it is per TENANT, and the multi-tenant chrome does not remount on a
//     tenant switch, so a workspace with AI on left the launcher standing over
//     a workspace that has it off. That one is a cross-tenant correctness bug,
//     not a cosmetic one.
// So the resolution is KEYED on the identity it belongs to — the tenant slug
// and the person, both already on the `identity` prop every host passes — plus
// a suite-wide refresh signal a switch's own call site can bump
// (`refreshLumiAvailability`, exported from this kit).
//
// Fail-closed on every re-resolution, not just the first: the key change resets
// the answer to `null` (panel unmounted) BEFORE the new lookup starts, and a
// stale in-flight response can no longer land on the new tenant — `token`
// makes a late answer for a previous key a no-op rather than an availability
// decision taken for the wrong workspace.
const aiAvailable = ref<boolean | null>(null);
const availabilityVersion = lumiAvailabilityVersion();
const availabilityKey = computed(() => {
  const id = ident();
  if (!isAuthenticated.value) return '';
  return `${id.tenantSlug ?? ''}|${id.userId ?? ''}|${availabilityVersion.value}`;
});
let availabilityToken = 0;
watch(
  availabilityKey,
  async (key) => {
    const token = ++availabilityToken;
    aiAvailable.value = null;
    if (!key) return;
    try {
      const result = await transport.request<{ enabled?: boolean }>('availability', { query: { module: props.module } });
      if (token !== availabilityToken) return;
      aiAvailable.value = !!result?.enabled;
    } catch {
      if (token !== availabilityToken) return;
      aiAvailable.value = false;
    }
  },
  { immediate: true },
);

// A link that asks for the panel: `?lumi=open` (#1014, ADR 0102 §4). A member's
// AI agent hands the person this link when an action it proposed waits on the
// panel, so the card is one click away instead of a hunt for the launcher. Read
// once, acted on once Lumi is known to be available (the shell registers its
// opener on mount), then removed from the URL so a reload is an ordinary page.
const openRequested = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('lumi') === 'open';
if (openRequested) {
  const stop = watch(aiAvailable, async (available) => {
    if (available !== true) return;
    stop();
    await nextTick();
    await nextTick();
    openLumiDock();
    const { lumi: _lumi, ...query } = router.currentRoute.value.query;
    void router.replace({ query });
  });
}
</script>

<template>
  <LumiAssistantPanel
    v-if="isAuthenticated && aiAvailable === true"
    :module="module"
    :transport="transport"
    :labels="labels"
    :mode-storage-key="keys.mode"
    :state-storage-key="keys.state"
    :conversation-storage-key="keys.conversation"
    :open-on-arrival="openOnArrival"
  />
</template>
